import { useDragAndDrop, DropIndicator, type Key } from "react-aria-components";
import { useDragAndDropContext } from "./DragAndDropContext";
import { categorySchema } from "@medoma/models/activities";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Text } from "@medoma/components/Text/Text";
import clsx from "clsx";
import styles from "../ShiftsRow.module.scss";
import { useSelectedDateHasPassed } from "@/Utils/useSelectedDate";
import { getItemLabel } from "./dragAndDropUtils";
import { routeStatusSchema, type IRoute } from "@medoma/models/routes";

type UseDragAndDropFromRouteParams = {
  routeItem: Pick<IRoute, "id" | "status" | "visits">;
  reorder: (params: { groupId: string; orderedIds: string[] }) => void;
  shiftId: number;
};

export const useDragAndDropFromRoute = ({
  routeItem,
  reorder,
  shiftId,
}: UseDragAndDropFromRouteParams) => {
  const { startDrag, endDrag } = useDragAndDropContext();
  const hasDatePassed = useSelectedDateHasPassed();

  const { id: routeId, status, visits } = routeItem;

  // Finished routes are not allowed to be altered in any way
  const isDragAndDropFromRouteNotAllowed =
    status === routeStatusSchema.enum.finished;

  return useDragAndDrop({
    isDisabled: isDragAndDropFromRouteNotAllowed,
    getItems: (keys) => {
      return [...keys].map((key) => {
        const visit = visits?.find((visit) => visit.id === key);

        if (!visit || !routeId) {
          return {
            "medoma-item": "",
            "text-label": "",
          };
        }

        return {
          "medoma-item": visit.id,
          "text-label": getItemLabel(visit),
        };
      });
    },
    onDragStart: (e) => {
      const draggedKeys = e.keys;
      const firstKey = draggedKeys.values().next().value;
      const draggedVisit = visits.find((visit) => visit.id === firstKey);

      if (!draggedVisit) {
        return;
      }

      startDrag({ source: "route", routeId, shiftId, item: draggedVisit });
    },
    onDragEnd: () => {
      endDrag();
    },
    onReorder: (e: {
      target: { dropPosition: string; key: Key };
      keys: Iterable<Key>;
    }) => {
      // Only allow reordering if there are multiple visits and date hasn't passed
      if (visits.length <= 1 || hasDatePassed) {
        return;
      }

      const visitIdsToMove = Array.from(e.keys);
      const targetVisitId = e.target.key;

      if (visitIdsToMove.includes(targetVisitId)) {
        return;
      }

      const visitsToMove = visits.filter((visit) =>
        visitIdsToMove.includes(visit.id),
      );

      const remainingVisits = visits.filter(
        (visit) => !visitIdsToMove.includes(visit.id),
      );

      const targetIndex = remainingVisits.findIndex(
        (visit) => visit.id === targetVisitId,
      );

      if (targetIndex === -1) {
        console.error("Invalid target key provided");
        return;
      }

      const dropPositionOffset =
        e.target.dropPosition === "before"
          ? 0
          : e.target.dropPosition === "after"
            ? 1
            : undefined;

      if (dropPositionOffset === undefined) {
        console.error("Invalid drop position provided");
        return;
      }

      const reorderedVisits = [
        ...remainingVisits.slice(0, targetIndex + dropPositionOffset),
        ...visitsToMove,
        ...remainingVisits.slice(targetIndex + dropPositionOffset),
      ];

      reorder({
        groupId: String(visitIdsToMove[0]),
        orderedIds: reorderedVisits.map((visit) => visit.id),
      });
    },
    renderDropIndicator(target) {
      // Don't show drop indicators if reordering is not allowed
      if (visits.length <= 1 || hasDatePassed) {
        return <></>;
      }
      return (
        <DropIndicator
          target={target}
          className={({ isDropTarget }: { isDropTarget: boolean }) =>
            clsx(styles.dropIndicator, isDropTarget && styles.dropTarget)
          }
        />
      );
    },
    renderDragPreview(items) {
      const itemBeingDragged = items?.[0];
      if (!itemBeingDragged) {
        return <></>;
      }
      const draggedName = itemBeingDragged["text-label"];
      return (
        <div className={styles.dragPreview}>
          <CategoryIcon category={categorySchema.enum.HomeVisit} size="small" />
          <Text element="p">{draggedName}</Text>
        </div>
      );
    },
  });
};
